import React, { useState, useEffect, useCallback, useMemo, useImperativeHandle } from 'react';
import { payrollService } from '../services/payrollService';
import { payrollClosureService } from '../services/payrollClosureService';
import './PayrollForm.css';

const formatEmployeeLabel = (emp) => {
  if (!emp) return '';
  const qual = emp.qualification || emp.poste || 'Qualification non spécifiée';
  return `${emp.nom || ''} ${emp.prenom || ''} — ${qual}`.trim();
};

const normalizeBonusName = (str) =>
  (str || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '');

/** Découpe la réponse API default-bonuses : primes hors lait + total prime de lait */
const processDefaultBonusesPayload = (rows) => {
  let milkTotal = 0;
  const bonuses = [];
  for (const b of rows || []) {
    const name = b.name || '';
    const amount = b.amount != null ? Number(b.amount) : null;
    if (amount === null || isNaN(amount)) continue;

    const normName = normalizeBonusName(name);
    const isMilk =
      normName.includes('primedelait') ||
      normName.includes('primelait') ||
      normName.includes('lait');

    if (isMilk) {
      milkTotal += amount;
    } else {
      bonuses.push({
        name,
        amount: amount.toFixed(3),
      });
    }
  }
  return { bonuses, milkTotal };
};

const isRendementMensuel = (name) => {
  const k = normalizeBonusName(name);
  return k.includes('rendement') && k.includes('mensuel');
};

const computeNormalizedWorkingDaysFromMonthlySummary = (summary) => {
  const workingDays = summary?.totalWorkingDays != null ? Number(summary.totalWorkingDays) : 0;
  const absentDays = summary?.totalAbsentDays != null ? Number(summary.totalAbsentDays) : 0;
  const leaveDays = summary?.totalLeaveDays != null ? Number(summary.totalLeaveDays) : 0;

  let joursTravail = (workingDays + leaveDays) - absentDays;
  if (!Number.isFinite(joursTravail) || joursTravail < 0) joursTravail = 0;

  // Règle 1 : Normalisation à 26 jours si aucune absence (aligné backend)
  if (joursTravail < 26) {
    if (absentDays === 0) {
      joursTravail = 26;
    }
  }

  // Règle 2 : Si (jours pointés + congés) > 26 → joursTravail = 26 - absences
  const joursTravaillesPlusConges = (workingDays + leaveDays);
  
    joursTravail = 26 - absentDays;
    if (joursTravail < 0) joursTravail = 0;
  

  return joursTravail;
};

/**
 * Jours utilisés pour proratiser le salaire de base (×/26) :
 * si « Jours travaillés » est renseigné, il prime sur le résumé mensuel pointage.
 */
const resolveProrationDaysForBase = (workedDaysStr, monthlyHoursSummary) => {
  if (workedDaysStr != null && String(workedDaysStr).trim() !== '') {
    const wd = parseFloat(workedDaysStr);
    if (Number.isFinite(wd) && wd >= 0) {
      return wd;
    }
  }
  if (monthlyHoursSummary) {
    return computeNormalizedWorkingDaysFromMonthlySummary(monthlyHoursSummary);
  }
  return null;
};

/** Une ligne de prêt : montant pour ce mois ou '' (hors période). */
const computeSingleLoanCreditForPeriod = (loan, year, month) => {
  if (!loan) return '';
  const total =
    loan.principalAmount != null ? Number(loan.principalAmount) : NaN;
  const term =
    loan.termMonths != null ? parseInt(loan.termMonths, 10) : NaN;
  const sy =
    loan.startYear != null ? parseInt(loan.startYear, 10) : NaN;
  const sm =
    loan.startMonth != null ? parseInt(loan.startMonth, 10) : NaN;
  if (
    !Number.isFinite(total) ||
    total <= 0 ||
    !Number.isFinite(term) ||
    term < 1 ||
    !Number.isFinite(sy) ||
    !Number.isFinite(sm) ||
    sm < 1 ||
    sm > 12 ||
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    month < 1 ||
    month > 12
  ) {
    return '';
  }
  const startIdx = sy * 12 + (sm - 1);
  const curIdx = year * 12 + (month - 1);
  const elapsed = curIdx - startIdx;
  if (elapsed < 0 || elapsed >= term) {
    return '';
  }
  const monthly = Math.round((total / term) * 1000) / 1000;
  if (elapsed === term - 1) {
    const paidBefore = Math.round(monthly * (term - 1) * 1000) / 1000;
    const last = Math.round((total - paidBefore) * 1000) / 1000;
    return last >= 0 ? last.toFixed(3) : '';
  }
  return monthly.toFixed(3);
};

/** Somme des mensualités actives (plusieurs prêts). */
const computeEmployeeLoanCreditForPeriod = (emp, year, month) => {
  if (!emp) return '';
  const list = emp.loans;
  if (!list || list.length === 0) return '';
  let sum = 0;
  let any = false;
  for (const loan of list) {
    const part = computeSingleLoanCreditForPeriod(loan, year, month);
    if (part !== '') {
      sum += parseFloat(part, 10);
      any = true;
    }
  }
  return any ? sum.toFixed(3) : '';
};

const PayrollForm = React.forwardRef(
  (
    {
      employeeId,
      employeeName,
      employees = [],
      payroll,
      onSave,
      onCancel,
      periodYear: forcedPeriodYear,
      periodMonth: forcedPeriodMonth,
      disablePeriodEdit = false,
      batchMode = false,
      titlePrefix = null,
      onPeriodChange = null,
    },
    ref
  ) => {
  const [formData, setFormData] = useState({
    employeeId: employeeId ? employeeId.toString() : '',
    periodYear: new Date().getFullYear(),
    periodMonth: new Date().getMonth() + 1,
    baseSalary: '',
    bonusesList: [],
    deductions: '',
    advance: '',
    credit: '',
    advantages: '',
    additionalDeductions: '',
    isCadre: false,
    applyCSS: true,
    milkAllowanceEnabled: false,
    milkAllowance: '',
    notes: '',
    // Heures de travail
    workedDays: '',
    totalWorkedHours: '',
    totalOvertimeHours: '',
    hoursManuallyAdjusted: false,
  });
  const [errors, setErrors] = useState({});
  const [calculations, setCalculations] = useState({
    grossSalary: 0,
    netSalary: 0,
    baseProrated: '0.000',
  });
  const [simulationResult, setSimulationResult] = useState(null);
  const [simulationNetPayable, setSimulationNetPayable] = useState(null);
  const [simulationError, setSimulationError] = useState('');
  const [isSimulating, setIsSimulating] = useState(false);
  const [isPeriodClosed, setIsPeriodClosed] = useState(false);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [creditManuallyAdjusted, setCreditManuallyAdjusted] = useState(false);
  const [monthlyHoursSummary, setMonthlyHoursSummary] = useState(null);
  const [annualLeaveDaysInPeriod, setAnnualLeaveDaysInPeriod] = useState(null);
  const [annualLeaveDaysError, setAnnualLeaveDaysError] = useState('');

  // Période forcée par un parent (mode multi-fiches)
  useEffect(() => {
    if (payroll) return;
    if (forcedPeriodYear == null || forcedPeriodYear === '') return;
    if (String(formData.periodYear) === String(forcedPeriodYear)) return;
    setFormData((prev) => ({ ...prev, periodYear: forcedPeriodYear }));
  }, [forcedPeriodYear, payroll]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (payroll) return;
    if (forcedPeriodMonth == null || forcedPeriodMonth === '') return;
    if (String(formData.periodMonth) === String(forcedPeriodMonth)) return;
    setFormData((prev) => ({ ...prev, periodMonth: forcedPeriodMonth }));
  }, [forcedPeriodMonth, payroll]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredEmployeesForForm = useMemo(() => {
    if (!employeeSearchText.trim()) return employees;
    const q = employeeSearchText.toLowerCase();
    return employees.filter((emp) => {
      const fullName = `${emp.nom || ''} ${emp.prenom || ''}`.toLowerCase();
      const matricule = (emp.matricule || '').toLowerCase();
      const cin = (emp.cin || '').toLowerCase();
      const cnss = (emp.cnssNumber || '').toLowerCase();
      const qual = `${emp.qualification || ''} ${emp.poste || ''}`.toLowerCase();
      return (
        fullName.includes(q) ||
        matricule.includes(q) ||
        cin.includes(q) ||
        cnss.includes(q) ||
        qual.includes(q)
      );
    });
  }, [employees, employeeSearchText]);

  useEffect(() => {
    if (employeeName) return;
    if (payroll && formData.employeeId && employees.length > 0) {
      const emp = employees.find((e) => String(e.id) === String(formData.employeeId));
      if (emp) setEmployeeSearchText(formatEmployeeLabel(emp));
    }
  }, [payroll?.id, employeeName, employees, formData.employeeId]);

  useEffect(() => {
    if (employeeName) return;
    if (!payroll && !formData.employeeId) {
      setEmployeeSearchText('');
    }
  }, [payroll, employeeName, formData.employeeId]);

  useEffect(() => {
    if (payroll) {
      // S'assurer que bonusesList est un tableau et que chaque élément a name et amount
      const bonusesList = (payroll.bonusesList || []).map(b => ({
        name: b.name || '',
        amount: b.amount?.toString() || ''
      }));
      
      console.log('Payroll chargé:', payroll);
      console.log('BonusesList:', bonusesList);
      
      setFormData({
        employeeId: payroll.employeeId,
        periodYear: payroll.periodYear,
        periodMonth: payroll.periodMonth,
        baseSalary: payroll.baseSalary?.toString() || '',
        bonusesList: bonusesList,
        deductions: payroll.deductions?.toString() || '',
        advance: payroll.advance?.toString() || '',
        credit: payroll.credit?.toString() || '',
        advantages: payroll.advantages?.toString() || '',
        additionalDeductions: payroll.additionalDeductions?.toString() || '',
        isCadre: payroll.isCadre ?? false,
        applyCSS: payroll.applyCSS ?? true,
        milkAllowanceEnabled: payroll.milkAllowance != null && payroll.milkAllowance > 0,
        milkAllowance: payroll.milkAllowance?.toString() || '',
        notes: payroll.notes || '',
        // Heures de travail
        workedDays: payroll.workedDays?.toString() || '',
        totalWorkedHours: payroll.totalWorkedHours?.toString() || '',
        totalOvertimeHours: payroll.totalOvertimeHours?.toString() || '',
        hoursManuallyAdjusted: payroll.hoursManuallyAdjusted ?? false,
      });
      setCalculations({
        grossSalary: payroll.grossSalary || 0,
        netSalary: payroll.netSalary || 0,
      });
      setCreditManuallyAdjusted(true);
    } else {
      setCreditManuallyAdjusted(false);
      if (employeeId) {
        setFormData((prev) => ({ ...prev, employeeId: employeeId.toString() }));
      }
    }
  }, [payroll, employeeId]);

  // Charger et afficher les primes par défaut proratisées (incluant automatiquement la prime HS si applicable)
  // lors de la sélection de l'employé et de la période, uniquement en création
  useEffect(() => {
    const loadDefaultBonuses = async () => {
      if (payroll) return; // en modification, on ne touche pas aux primes existantes
      if (!formData.employeeId || !formData.periodYear || !formData.periodMonth) return;
      if (formData.bonusesList && formData.bonusesList.length > 0) return; // ne pas écraser des saisies manuelles

      try {
        const wd = parseInt(formData.workedDays, 10);
        const preview = {
          includeOvertime: true,
          baseSalary: formData.baseSalary,
          workedDays: Number.isFinite(wd) && wd > 0 ? wd : undefined,
          overtimeHours: formData.totalOvertimeHours,
        };
        const response = await payrollService.getDefaultBonuses(
          parseInt(formData.employeeId, 10),
          parseInt(formData.periodYear, 10),
          parseInt(formData.periodMonth, 10),
          preview
        );

        const { bonuses, milkTotal } = processDefaultBonusesPayload(response.data);

        if (bonuses.length > 0 || milkTotal > 0) {
          setFormData((prev) => ({
            ...prev,
            bonusesList: bonuses,
            milkAllowanceEnabled: milkTotal > 0 ? true : prev.milkAllowanceEnabled,
            milkAllowance: milkTotal > 0 ? milkTotal.toFixed(3) : prev.milkAllowance,
          }));
        }
      } catch (err) {
        console.error('Erreur lors du chargement des primes par défaut:', err);
      }
    };

    loadDefaultBonuses();
  }, [
    formData.employeeId,
    formData.periodYear,
    formData.periodMonth,
    formData.baseSalary,
    formData.workedDays,
    formData.totalOvertimeHours,
    payroll,
  ]);

  const recalculateRendementBonus = useCallback(async () => {
    if (!formData.employeeId || !formData.periodYear || !formData.periodMonth) return;
    if (!formData.baseSalary || parseFloat(formData.baseSalary) <= 0) return;
    if (isPeriodClosed) return;

    try {
      const wd = parseInt(formData.workedDays, 10);
      const preview = {
        includeOvertime: true,
        baseSalary: formData.baseSalary,
        workedDays: Number.isFinite(wd) && wd > 0 ? wd : undefined,
        overtimeHours: formData.totalOvertimeHours,
      };
      const response = await payrollService.getDefaultBonuses(
        parseInt(formData.employeeId, 10),
        parseInt(formData.periodYear, 10),
        parseInt(formData.periodMonth, 10),
        preview
      );
      const { bonuses } = processDefaultBonusesPayload(response.data);
      const rendementFromApi = bonuses.find((b) => isRendementMensuel(b.name));
      if (!rendementFromApi) return;

      setFormData((prev) => {
        const list = [...(prev.bonusesList || [])];
        const idx = list.findIndex((b) => isRendementMensuel(b.name));
        if (idx >= 0) {
          list[idx] = { ...list[idx], name: rendementFromApi.name, amount: rendementFromApi.amount };
        } else {
          list.push({ name: rendementFromApi.name, amount: rendementFromApi.amount });
        }
        return { ...prev, bonusesList: list };
      });
    } catch (err) {
      console.error('Erreur recalcul prime de rendement mensuel:', err);
    }
  }, [
    formData.employeeId,
    formData.periodYear,
    formData.periodMonth,
    formData.baseSalary,
    formData.workedDays,
    formData.totalOvertimeHours,
    isPeriodClosed,
  ]);

  // En édition : recalculer la prime de rendement mensuel quand le salaire de base ou les jours changent
  useEffect(() => {
    if (!payroll) return;
    if (!formData.employeeId || !formData.periodYear || !formData.periodMonth) return;
    if (!formData.baseSalary) return;

    const timer = setTimeout(() => {
      recalculateRendementBonus();
    }, 400);

    return () => clearTimeout(timer);
  }, [
    payroll,
    formData.employeeId,
    formData.periodYear,
    formData.periodMonth,
    formData.baseSalary,
    formData.workedDays,
    recalculateRendementBonus,
  ]);

  const reloadDefaultBonuses = useCallback(
    async (includeOvertimePreview) => {
      if (!formData.employeeId || !formData.periodYear || !formData.periodMonth) {
        setErrors((prev) => ({
          ...prev,
          bonuses: 'Veuillez sélectionner un employé et une période pour recharger les primes',
        }));
        return;
      }
      if (isPeriodClosed) return;
      try {
        let preview = null;
        if (includeOvertimePreview) {
          const wd = parseInt(formData.workedDays, 10);
          preview = {
            includeOvertime: true,
            baseSalary: formData.baseSalary,
            workedDays: Number.isFinite(wd) && wd > 0 ? wd : undefined,
            overtimeHours: formData.totalOvertimeHours,
          };
        }
        const response = await payrollService.getDefaultBonuses(
          parseInt(formData.employeeId, 10),
          parseInt(formData.periodYear, 10),
          parseInt(formData.periodMonth, 10),
          preview
        );
        const { bonuses, milkTotal } = processDefaultBonusesPayload(response.data);
        if (includeOvertimePreview) {
          setFormData((prev) => ({
            ...prev,
            bonusesList: bonuses,
            milkAllowanceEnabled: milkTotal > 0 ? true : prev.milkAllowanceEnabled,
            milkAllowance: milkTotal > 0 ? milkTotal.toFixed(3) : prev.milkAllowance,
          }));
        }
        setErrors((prev) => ({ ...prev, bonuses: null }));
      } catch (err) {
        console.error('Erreur lors du rechargement des primes:', err);
        setErrors((prev) => ({
          ...prev,
          bonuses:
            err.response?.data?.error || 'Erreur lors du rechargement des primes',
        }));
      }
    },
    [
      formData.employeeId,
      formData.periodYear,
      formData.periodMonth,
      formData.baseSalary,
      formData.workedDays,
      formData.totalOvertimeHours,
      isPeriodClosed,
    ]
  );

  // Préremplir le baseTH et le salaire de base depuis l'employé sélectionné (fallback si changement via props)
  useEffect(() => {
    if (!payroll && formData.employeeId && employees.length > 0) {
      const selectedEmployee = employees.find(
        (emp) => String(emp.id) === String(formData.employeeId)
      );
      if (selectedEmployee && selectedEmployee.baseTH != null) {
        // Préremplir seulement si le champ est vide (pour ne pas écraser une modification manuelle)
        const employeeBaseTH = selectedEmployee.baseTH.toString();
        setFormData(prev => {
          if (!prev.baseSalary || prev.baseSalary === '') {
            return { ...prev, baseSalary: employeeBaseTH };
          }
          return prev;
        });
      }
    }
  }, [formData.employeeId, employees, payroll]);

  const calculateSalaries = useCallback(() => {
    const baseMonthly = parseFloat(formData.baseSalary) || 0;
    const bonuses = formData.bonusesList.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
    const advantages = parseFloat(formData.advantages) || 0;
    const deductions = parseFloat(formData.deductions) || 0;
    const additionalDeductions = parseFloat(formData.additionalDeductions) || 0;
    const advance = parseFloat(formData.advance) || 0;
    const credit = parseFloat(formData.credit) || 0;
    const milkAllowance = formData.milkAllowanceEnabled ? (parseFloat(formData.milkAllowance) || 0) : 0;

    // Salaire de base ajusté : base mensuelle × (jours / 26)
    // Le champ « Jours travaillés » prime sur le résumé pointage s'il est renseigné.
    let baseAdjusted = baseMonthly;
    const prorationDays = resolveProrationDaysForBase(
      formData.workedDays,
      monthlyHoursSummary
    );
    if (
      prorationDays != null &&
      Number.isFinite(prorationDays) &&
      prorationDays >= 0 &&
      baseMonthly > 0
    ) {
      baseAdjusted = (baseMonthly * prorationDays) / 26;
    }

    // Brut = salaire de base ajusté + primes + avantages imposables
    const gross = baseAdjusted + bonuses + advantages;
    // Aperçu net simplifié (hors moteur légal) : retenues + déductions add., avance et crédit
    let net = Math.max(0, gross - deductions - additionalDeductions - advance - credit);
    net += milkAllowance;

    setCalculations({
      grossSalary: gross.toFixed(3),
      netSalary: net.toFixed(3),
      baseProrated: baseAdjusted.toFixed(3),
    });
  }, [
    formData.baseSalary,
    formData.bonusesList,
    formData.deductions,
    formData.advance,
    formData.credit,
    formData.advantages,
    formData.additionalDeductions,
    formData.milkAllowanceEnabled,
    formData.milkAllowance,
    formData.workedDays,
    monthlyHoursSummary,
  ]);

  useEffect(() => {
    calculateSalaries();
  }, [calculateSalaries]);

  const validate = () => {
    const newErrors = {};

    if (!formData.employeeId) {
      newErrors.employeeId = 'L\'employé est obligatoire';
    }

    if (!formData.baseSalary || parseFloat(formData.baseSalary) <= 0) {
      newErrors.baseSalary = 'Le salaire de base doit être supérieur à 0';
    }

    // Validation des primes
    if (formData.bonusesList && formData.bonusesList.some(b => parseFloat(b.amount) < 0)) {
      newErrors.bonuses = 'Les valeurs des primes ne peuvent pas être négatives';
    }

    if (formData.deductions && parseFloat(formData.deductions) < 0) {
      newErrors.deductions = 'Les retenues ne peuvent pas être négatives';
    }

    if (formData.advance && parseFloat(formData.advance) < 0) {
      newErrors.advance = 'L\'avance ne peut pas être négative';
    }

    if (formData.credit && parseFloat(formData.credit) < 0) {
      newErrors.credit = 'Le crédit ne peut pas être négatif';
    }

    const bonuses = formData.bonusesList.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
    const advantages = parseFloat(formData.advantages) || 0;
    let baseAdjusted = parseFloat(formData.baseSalary) || 0;
    const prorationDays = resolveProrationDaysForBase(
      formData.workedDays,
      monthlyHoursSummary
    );
    if (
      prorationDays != null &&
      Number.isFinite(prorationDays) &&
      prorationDays >= 0 &&
      baseAdjusted > 0
    ) {
      baseAdjusted = (baseAdjusted * prorationDays) / 26;
    }
    const gross =
      baseAdjusted + bonuses + advantages;
    if (formData.deductions && parseFloat(formData.deductions) > gross) {
      newErrors.deductions = 'Les retenues ne peuvent pas dépasser le salaire brut';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    setSimulationResult(null);
    setSimulationNetPayable(null);
    setSimulationError('');
  }, [
    formData.employeeId,
    formData.baseSalary,
    formData.workedDays,
    formData.bonusesList,
    formData.advantages,
    formData.additionalDeductions,
    formData.isCadre,
    formData.applyCSS,
  ]);

  // Vérifier si la période est clôturée
  useEffect(() => {
    const checkPeriod = async () => {
      if (formData.periodYear && formData.periodMonth) {
        try {
          const response = await payrollClosureService.checkPeriod(
            parseInt(formData.periodYear),
            parseInt(formData.periodMonth)
          );
          setIsPeriodClosed(response.data.isClosed);
        } catch (err) {
          console.error('Error checking period closure:', err);
          setIsPeriodClosed(false);
        }
      }
    };
    checkPeriod();
  }, [formData.periodYear, formData.periodMonth]);

  const loadHoursFromTimesheets = useCallback(async () => {
    const eid = formData.employeeId;
    const y = formData.periodYear;
    const m = formData.periodMonth;
    if (!eid || y === '' || y === undefined || m === '' || m === undefined) return;
    if (isPeriodClosed) return;
    try {
      const response = await payrollService.getMonthlyHours(
        parseInt(eid, 10),
        parseInt(y, 10),
        parseInt(m, 10)
      );
      const summary = response.data;
      setMonthlyHoursSummary(summary);
      const workedDays = computeNormalizedWorkingDaysFromMonthlySummary(summary);
      setFormData((prev) => ({
        ...prev,
        workedDays: workedDays && workedDays > 0 ? workedDays.toString() : '',
        totalWorkedHours: summary.totalWorkedHours?.toString() || '',
        totalOvertimeHours: summary.totalOvertimeHours?.toString() || '',
        hoursManuallyAdjusted: false,
      }));
      setErrors((prev) => ({ ...prev, hours: null }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        hours: err.response?.data?.error || 'Erreur lors du chargement des heures depuis les pointages',
      }));
    }
  }, [formData.employeeId, formData.periodYear, formData.periodMonth, isPeriodClosed]);

  const loadAnnualLeaveDaysForPeriod = useCallback(async () => {
    const eid = formData.employeeId;
    const y = formData.periodYear;
    const m = formData.periodMonth;
    if (!eid || y === '' || y === undefined || m === '' || m === undefined) return;
    try {
      setAnnualLeaveDaysError('');
      const response = await payrollService.getAnnualLeaveDays(
        parseInt(eid, 10),
        parseInt(y, 10),
        parseInt(m, 10)
      );
      const days = response?.data?.annualLeaveDays;
      setAnnualLeaveDaysInPeriod(days != null ? Number(days) : 0);
    } catch (err) {
      setAnnualLeaveDaysInPeriod(null);
      setAnnualLeaveDaysError(
        err.response?.data?.error || 'Erreur lors du chargement des congés annuels'
      );
    }
  }, [formData.employeeId, formData.periodYear, formData.periodMonth]);

  // Création uniquement : recharger jours / heures / HS depuis le pointage quand employé ou période change
  useEffect(() => {
    if (payroll) return;
    if (!formData.employeeId || !formData.periodYear || !formData.periodMonth) return;
    if (isPeriodClosed) return;
    loadHoursFromTimesheets();
  }, [
    payroll,
    formData.employeeId,
    formData.periodYear,
    formData.periodMonth,
    isPeriodClosed,
    loadHoursFromTimesheets,
  ]);

  // Charger le nombre de jours de congé annuel approuvés pour la période sélectionnée
  useEffect(() => {
    if (!formData.employeeId || !formData.periodYear || !formData.periodMonth) {
      setAnnualLeaveDaysInPeriod(null);
      setAnnualLeaveDaysError('');
      return;
    }
    loadAnnualLeaveDaysForPeriod();
  }, [formData.employeeId, formData.periodYear, formData.periodMonth, loadAnnualLeaveDaysForPeriod]);

  const selectedEmployee = employees.find(
    (emp) => String(emp.id) === String(formData.employeeId)
  );
  const missingLegalData = selectedEmployee && (!selectedEmployee.cin || !selectedEmployee.cnssNumber);

  const employeeLoanSignature = useMemo(() => {
    const emp = employees.find((e) => String(e.id) === String(formData.employeeId));
    if (!emp) return '';
    const rows = (emp.loans || []).map((l) =>
      [
        l.id ?? '',
        l.principalAmount ?? '',
        l.termMonths ?? '',
        l.startYear ?? '',
        l.startMonth ?? '',
        l.label ?? '',
      ].join(':')
    );
    return `${emp.id}|${rows.join(';')}`;
  }, [employees, formData.employeeId]);

  // Création : crédit depuis le prêt employé (fiche employé) selon la période
  useEffect(() => {
    if (payroll) return;
    if (creditManuallyAdjusted) return;
    if (!formData.employeeId || !formData.periodYear || !formData.periodMonth) return;
    if (isPeriodClosed) return;
    const emp = employees.find((e) => String(e.id) === String(formData.employeeId));
    if (!emp) return;
    const y = parseInt(formData.periodYear, 10);
    const m = parseInt(formData.periodMonth, 10);
    if (!Number.isFinite(y) || !Number.isFinite(m)) return;
    const creditStr = computeEmployeeLoanCreditForPeriod(emp, y, m);
    setFormData((prev) => ({ ...prev, credit: creditStr }));
  }, [
    payroll,
    creditManuallyAdjusted,
    employeeLoanSignature,
    formData.periodYear,
    formData.periodMonth,
    isPeriodClosed,
  ]);

  const handlePickEmployee = (emp) => {
    setCreditManuallyAdjusted(false);
    setFormData((prev) => ({
      ...prev,
      employeeId: String(emp.id),
      baseSalary: emp.baseTH != null ? emp.baseTH.toString() : '',
    }));
    setEmployeeSearchText(formatEmployeeLabel(emp));
    setEmployeeDropdownOpen(false);
  };

  const handleClearEmployeePick = () => {
    setCreditManuallyAdjusted(false);
    setFormData((prev) => ({
      ...prev,
      employeeId: '',
      baseSalary: '',
      credit: '',
    }));
    setEmployeeSearchText('');
    setEmployeeDropdownOpen(false);
  };

  const handleEmployeeSearchChange = (e) => {
    const v = e.target.value;
    setEmployeeSearchText(v);
    setEmployeeDropdownOpen(true);
    const current = employees.find((emp) => String(emp.id) === String(formData.employeeId));
    if (current && formatEmployeeLabel(current) !== v) {
      setCreditManuallyAdjusted(false);
      setFormData((prev) => ({ ...prev, employeeId: '', baseSalary: '', credit: '' }));
    }
    if (!v.trim()) {
      setCreditManuallyAdjusted(false);
      setFormData((prev) => ({ ...prev, employeeId: '', baseSalary: '', credit: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const payrollData = {
        employeeId: parseInt(formData.employeeId),
        periodYear: parseInt(formData.periodYear),
        periodMonth: parseInt(formData.periodMonth),
        baseSalary: parseFloat(formData.baseSalary),
        bonusesList: formData.bonusesList.map(b => ({
          name: b.name || '',
          amount: parseFloat(b.amount) || 0
        })),
        deductions: formData.deductions ? parseFloat(formData.deductions) : 0,
        advance: formData.advance ? parseFloat(formData.advance) : 0,
        credit: formData.credit ? parseFloat(formData.credit) : 0,
        milkAllowance: formData.milkAllowanceEnabled && formData.milkAllowance ? parseFloat(formData.milkAllowance) : 0,
        notes: formData.notes,
        // Heures de travail
        workedDays: formData.workedDays ? parseInt(formData.workedDays) : null,
        totalWorkedHours: formData.totalWorkedHours ? parseFloat(formData.totalWorkedHours) : null,
        totalOvertimeHours: formData.totalOvertimeHours ? parseFloat(formData.totalOvertimeHours) : null,
        hoursManuallyAdjusted: formData.hoursManuallyAdjusted,
      };

      if (payroll) {
        await payrollService.update(payroll.id, payrollData);
      } else {
        await payrollService.create(payrollData);
      }

      onSave();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de l\'enregistrement';
      setErrors({ submit: errorMessage });
    }
  };

  useImperativeHandle(ref, () => ({
    validate,
    getPayrollData: () => ({
      employeeId: parseInt(formData.employeeId, 10),
      periodYear: parseInt(formData.periodYear, 10),
      periodMonth: parseInt(formData.periodMonth, 10),
      baseSalary: parseFloat(formData.baseSalary),
      bonusesList: (formData.bonusesList || []).map((b) => ({
        name: b.name || '',
        amount: parseFloat(b.amount) || 0,
      })),
      deductions: formData.deductions ? parseFloat(formData.deductions) : 0,
      advance: formData.advance ? parseFloat(formData.advance) : 0,
      credit: formData.credit ? parseFloat(formData.credit) : 0,
      advantages: formData.advantages ? parseFloat(formData.advantages) : 0,
      additionalDeductions: formData.additionalDeductions ? parseFloat(formData.additionalDeductions) : 0,
      milkAllowance:
        formData.milkAllowanceEnabled && formData.milkAllowance ? parseFloat(formData.milkAllowance) : 0,
      notes: formData.notes,
      workedDays: formData.workedDays ? parseInt(formData.workedDays, 10) : null,
      totalWorkedHours: formData.totalWorkedHours ? parseFloat(formData.totalWorkedHours) : null,
      totalOvertimeHours: formData.totalOvertimeHours ? parseFloat(formData.totalOvertimeHours) : null,
      hoursManuallyAdjusted: formData.hoursManuallyAdjusted,
      isCadre: formData.isCadre,
      applyCSS: formData.applyCSS,
    }),
  }));

  const handleSimulate = async () => {
    if (!selectedEmployee) {
      setSimulationError('Veuillez sélectionner un employé.');
      return;
    }
    if (missingLegalData) {
      setSimulationError('Cet employé doit avoir un CIN et un numéro CNSS pour lancer le calcul.');
      return;
    }

    const bonuses = formData.bonusesList.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
    let baseAdjusted = parseFloat(formData.baseSalary) || 0;
    const prorationDays = resolveProrationDaysForBase(
      formData.workedDays,
      monthlyHoursSummary
    );
    if (
      prorationDays != null &&
      Number.isFinite(prorationDays) &&
      prorationDays >= 0 &&
      baseAdjusted > 0
    ) {
      baseAdjusted = (baseAdjusted * prorationDays) / 26;
    }
    const grossSalary = baseAdjusted + bonuses;

    if (grossSalary <= 0) {
      setSimulationError('Le salaire brut doit être supérieur à 0 pour lancer le calcul.');
      return;
    }

    const payload = {
      employeeCin: selectedEmployee.cin,
      cnssNumber: selectedEmployee.cnssNumber,
      grossSalary,
      isCadre: formData.isCadre,
      maritalStatus: selectedEmployee.maritalStatus || 'SINGLE',
      children: selectedEmployee.children ?? 0,
      applyCSS: formData.applyCSS,
      primes: bonuses,
      advantages: parseFloat(formData.advantages) || 0,
      additionalDeductions: parseFloat(formData.additionalDeductions) || 0,
      month: parseInt(formData.periodMonth, 10),
      year: parseInt(formData.periodYear, 10),
    };

    setIsSimulating(true);
    setSimulationError('');
    try {
      const response = await payrollService.calculateNetSalary(payload);
      const result = response.data;
      
      // Calculer le net à payer (comme le backend) :
      // - Déduire avance + crédit après calcul légal (IRPP/CSS)
      // - Puis ajouter la prime de lait (exonérée)
      const milkAllowance = formData.milkAllowanceEnabled ? (parseFloat(formData.milkAllowance) || 0) : 0;
      const advance = parseFloat(formData.advance) || 0;
      const credit = parseFloat(formData.credit) || 0;
      const legalNet = result.netSalary || 0;
      setSimulationNetPayable(legalNet - advance - credit + milkAllowance);
      
      setSimulationResult(result);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        (err.response?.data && typeof err.response.data === 'object'
          ? Object.values(err.response.data).join(', ')
          : 'Erreur lors du calcul du salaire net');
      setSimulationError(errorMessage);
      setSimulationResult(null);
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payroll-form">
      <h3>
        {titlePrefix ? `${titlePrefix} — ` : ''}
        {payroll ? 'Modifier la paie' : 'Nouvelle paie'}
      </h3>
      <div className="form-row">
        <div className="form-group">
          <label>Année <span className="required">*</span>:</label>
          <input
            type="number"
            value={formData.periodYear}
            onChange={(e) => {
              const v = e.target.value;
              setFormData({ ...formData, periodYear: v });
              if (onPeriodChange) {
                onPeriodChange({ year: v, month: formData.periodMonth });
              }
            }}
            min="2020"
            max="2100"
            required
            disabled={isPeriodClosed || disablePeriodEdit}
          />
          {errors.periodYear && <span className="error">{errors.periodYear}</span>}
        </div>

        <div className="form-group">
          <label>Mois <span className="required">*</span>:</label>
          <select
            value={formData.periodMonth}
            onChange={(e) => {
              const v = e.target.value;
              setFormData({ ...formData, periodMonth: v });
              if (onPeriodChange) {
                onPeriodChange({ year: formData.periodYear, month: v });
              }
            }}
            required
            disabled={isPeriodClosed || disablePeriodEdit}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
              <option key={month} value={month}>
                {new Date(2000, month - 1).toLocaleString('fr-FR', { month: 'long' })}
              </option>
            ))}
          </select>
        </div>
      </div>
      {employeeName ? (
        <div className="form-group">
          <label>Employé:</label>
          <input type="text" value={employeeName} disabled />
        </div>
      ) : (
        <div className="form-group">
          <label htmlFor="payroll-employee-autocomplete">
            Employé <span className="required">*</span>:
          </label>
          <div className="payroll-employee-autocomplete">
            <div className="employee-select-wrapper">
              <input
                id="payroll-employee-autocomplete"
                type="text"
                className="employee-search-input"
                autoComplete="off"
                placeholder="Rechercher (nom, prénom, matricule, CIN, CNSS, qualification)…"
                value={employeeSearchText}
                onChange={handleEmployeeSearchChange}
                onFocus={() => setEmployeeDropdownOpen(true)}
                onBlur={() => setTimeout(() => setEmployeeDropdownOpen(false), 180)}
                disabled={isPeriodClosed}
              />
              {formData.employeeId && (
                <button
                  type="button"
                  className="clear-employee-btn"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleClearEmployeePick}
                  title="Effacer"
                  disabled={isPeriodClosed}
                >
                  ×
                </button>
              )}
              {employeeDropdownOpen && filteredEmployeesForForm.length > 0 && (
                <div
                  className="employee-dropdown"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {filteredEmployeesForForm.slice(0, 15).map((emp) => (
                    <div
                      key={emp.id}
                      className={`dropdown-item ${String(formData.employeeId) === String(emp.id) ? 'selected' : ''}`}
                      onClick={() => handlePickEmployee(emp)}
                      role="option"
                    >
                      <div className="employee-option">
                        <span className="employee-name">
                          {emp.nom} {emp.prenom}
                        </span>
                        {emp.matricule && (
                          <span className="employee-matricule">{emp.matricule}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {errors.employeeId && <span className="error">{errors.employeeId}</span>}
        </div>
      )}

      {isPeriodClosed && (
        <div className="error-banner">
          ⚠️ Cette période est clôturée. Vous ne pouvez pas modifier ou créer de fiches de paie pour cette période. Contactez un administrateur RH pour réouvrir la période si nécessaire.
        </div>
      )}

      {selectedEmployee && missingLegalData && (
        <div className="warning-banner">
          Les informations fiscales de cet employé sont incomplètes (CIN ou numéro CNSS manquant). Veuillez compléter sa fiche avant de lancer un calcul de paie.
        </div>
      )}

      

      <div className="form-group">
        <label>Salaire de base <span className="required">*</span>:</label>
        <input
          type="number"
          step="0.01"
          value={formData.baseSalary}
          onChange={(e) => setFormData({ ...formData, baseSalary: e.target.value })}
          required
        />
        {errors.baseSalary && <span className="error">{errors.baseSalary}</span>}
      </div>

      {/* Section Heures de travail */}
      <div className="form-section">
        <h4>Heures de travail</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Jours travaillés:</label>
            <input
              type="number"
              step="1"
              min="0"
              value={formData.workedDays}
              onChange={(e) => {
                setFormData({ 
                  ...formData, 
                  workedDays: e.target.value,
                  hoursManuallyAdjusted: true 
                });
              }}
              placeholder="Auto depuis pointage"
            />
          </div>
          <div className="form-group">
            <label>Heures travaillées:</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.totalWorkedHours}
              onChange={(e) => {
                setFormData({ 
                  ...formData, 
                  totalWorkedHours: e.target.value,
                  hoursManuallyAdjusted: true 
                });
              }}
              placeholder="Auto depuis pointage"
            />
          </div>
          <div className="form-group">
            <label>Heures supplémentaires:</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.totalOvertimeHours}
              onChange={(e) => {
                setFormData({ 
                  ...formData, 
                  totalOvertimeHours: e.target.value,
                  hoursManuallyAdjusted: true 
                });
              }}
              placeholder="Auto depuis pointage"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Congés annuels (période sélectionnée):</label>
            <input
              type="text"
              value={
                annualLeaveDaysInPeriod == null
                  ? ''
                  : `${annualLeaveDaysInPeriod} jour(s)`
              }
              placeholder="—"
              disabled
            />
            {annualLeaveDaysError && (
              <span className="error">{annualLeaveDaysError}</span>
            )}
          </div>
        </div>
        <div className="form-group">
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => {
              if (!formData.employeeId || !formData.periodYear || !formData.periodMonth) {
                setErrors((prev) => ({
                  ...prev,
                  hours: 'Veuillez sélectionner un employé et une période',
                }));
                return;
              }
              loadHoursFromTimesheets();
            }}
            disabled={!formData.employeeId || !formData.periodYear || !formData.periodMonth || isPeriodClosed}
          >
            📥 Charger depuis les pointages
          </button>
          {formData.hoursManuallyAdjusted && (
            <span className="info-text" style={{ marginLeft: '10px', color: '#666', fontSize: '0.9em' }}>
              ⚠️ Heures modifiées manuellement
            </span>
          )}
        </div>
        {errors.hours && <span className="error">{errors.hours}</span>}
      </div>

      <div className="form-group">
        <div className="payroll-primes-header">
          <label>Primes:</label>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            title="Recharge les primes proratisées (rendement mensuel recalculé selon le salaire de base) et les heures supplémentaires"
            onClick={() => reloadDefaultBonuses(true)}
            disabled={
              !formData.employeeId ||
              !formData.periodYear ||
              !formData.periodMonth ||
              isPeriodClosed
            }
          >
            Recharger les primes
          </button>
        </div>
        <div className="bonuses-list">
          {formData.bonusesList.map((bonus, index) => (
            <div key={index} className="bonus-item">
              <input
                type="text"
                placeholder="Nom de la prime"
                value={bonus.name || ''}
                onChange={(e) => {
                  const newBonuses = [...formData.bonusesList];
                  newBonuses[index] = { ...newBonuses[index], name: e.target.value };
                  setFormData({ ...formData, bonusesList: newBonuses });
                }}
                className="bonus-name"
              />
              <input
                type="number"
                step="0.001"
                placeholder="Montant"
                value={bonus.amount || ''}
                onChange={(e) => {
                  const newBonuses = [...formData.bonusesList];
                  newBonuses[index] = { ...newBonuses[index], amount: e.target.value };
                  setFormData({ ...formData, bonusesList: newBonuses });
                }}
                className="bonus-amount"
              />
              <button
                type="button"
                onClick={() => {
                  const newBonuses = formData.bonusesList.filter((_, i) => i !== index);
                  setFormData({ ...formData, bonusesList: newBonuses });
                }}
                className="btn-remove"
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              setFormData({
                ...formData,
                bonusesList: [...formData.bonusesList, { name: '', amount: '' }]
              });
            }}
            className="btn btn-outline btn-add-bonus"
          >
            + Ajouter une prime
          </button>
        </div>
        {errors.bonuses && <span className="error">{errors.bonuses}</span>}
      </div>

      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={formData.milkAllowanceEnabled}
            onChange={(e) => {
              setFormData({
                ...formData,
                milkAllowanceEnabled: e.target.checked,
                milkAllowance: e.target.checked ? formData.milkAllowance : ''
              });
            }}
            style={{ marginRight: '8px' }}
          />
          Prime de lait (exonérée d'impôt et CNSS):
        </label>
        {formData.milkAllowanceEnabled && (
          <input
            type="number"
            step="0.001"
            placeholder="Montant de la prime de lait"
            value={formData.milkAllowance}
            onChange={(e) => {
              setFormData({ ...formData, milkAllowance: e.target.value });
            }}
            min="0"
          />
        )}
      </div>

      <div className="form-group">
        <label>Retenues:</label>
        <input
          type="number"
          step="0.01"
          value={formData.deductions}
          onChange={(e) => setFormData({ ...formData, deductions: e.target.value })}
        />
        {errors.deductions && <span className="error">{errors.deductions}</span>}
      </div>

      <div className="form-group">
        <label>Avance sur salaire:</label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={formData.advance}
          onChange={(e) => setFormData({ ...formData, advance: e.target.value })}
        />
        {errors.advance && <span className="error">{errors.advance}</span>}
      </div>

      <div className="form-group">
        <label>
          Crédit (prêt / retenue)
          {!payroll &&
            selectedEmployee &&
            computeEmployeeLoanCreditForPeriod(
              selectedEmployee,
              parseInt(formData.periodYear, 10),
              parseInt(formData.periodMonth, 10)
            ) && (
              <span className="info-text" style={{ fontWeight: 'normal', marginLeft: '6px' }}>
                — prérempli depuis la fiche employé
              </span>
            )}
        </label>
        <input
          type="number"
          step="0.001"
          min="0"
          value={formData.credit}
          onChange={(e) => {
            setCreditManuallyAdjusted(true);
            setFormData({ ...formData, credit: e.target.value });
          }}
        />
        {errors.credit && <span className="error">{errors.credit}</span>}
      </div>

      <div className="form-group">
        <label>Avantages imposables:</label>
        <input
          type="number"
          step="0.01"
          value={formData.advantages}
          onChange={(e) => setFormData({ ...formData, advantages: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>Déductions additionnelles:</label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={formData.additionalDeductions}
          onChange={(e) => setFormData({ ...formData, additionalDeductions: e.target.value })}
        />
      </div>

      <div className="form-row">
        <div className="form-group toggle-group">
          <label>Statut cadre ?</label>
          <label className="switch">
            <input
              type="checkbox"
              checked={formData.isCadre}
              onChange={(e) => setFormData({ ...formData, isCadre: e.target.checked })}
            />
            <span className="slider" />
          </label>
        </div>
        <div className="form-group toggle-group">
          <label>Appliquer la CSS (1%)</label>
          <label className="switch">
            <input
              type="checkbox"
              checked={formData.applyCSS}
              onChange={(e) => setFormData({ ...formData, applyCSS: e.target.checked })}
            />
            <span className="slider" />
          </label>
        </div>
      </div>
      <div className="simulation-actions">
        <button
          type="button"
          className="btn btn-outline"
          onClick={handleSimulate}
          disabled={isSimulating || !selectedEmployee || missingLegalData}
        >
          {isSimulating ? 'Calcul en cours...' : 'Calculer le salaire net (Tunisie)'}
        </button>
      </div>
      

      

      {simulationError && (
        <div className="error-banner">
          {simulationError}
        </div>
      )}

      {simulationResult && (
        <div className="simulation-results">
          <h4>Résultats légaux</h4>
          <div className="result-grid">
            <div className="result-card">
              <span>CNSS</span>
              <strong>{simulationResult.cnss?.toFixed(3)} TND</strong>
            </div>
            <div className="result-card">
              <span>Frais professionnels</span>
              <strong>{simulationResult.fraisProfessionnels?.toFixed(3)} TND</strong>
            </div>
            <div className="result-card">
              <span>RNI</span>
              <strong>{simulationResult.revenuNetImposable?.toFixed(3)} TND</strong>
            </div>
            <div className="result-card">
              <span>Déductions familiales</span>
              <strong>{simulationResult.deductionsFamiliales?.toFixed(3)} TND</strong>
            </div>
            <div className="result-card">
              <span>IRPP</span>
              <strong>{simulationResult.irpp?.toFixed(3)} TND</strong>
            </div>
            <div className="result-card">
              <span>CSS</span>
              <strong>{simulationResult.css?.toFixed(3)} TND</strong>
            </div>
            <div className="result-card">
              <span>Salaire net légal (avant prime de lait)</span>
              <strong>{(simulationResult.netSalary || 0).toFixed(3)} TND</strong>
            </div>
            {formData.milkAllowanceEnabled && formData.milkAllowance && parseFloat(formData.milkAllowance) > 0 && (
              <div className="result-card">
                <span>Prime de lait </span>
                <strong>{parseFloat(formData.milkAllowance).toFixed(3)} TND</strong>
              </div>
            )}
            <div className="result-card highlight">
              <span>Salaire net total</span>
              <strong>{(simulationNetPayable ?? (simulationResult.netSalary || 0)).toFixed(0)} TND</strong>
            </div>
          </div>
        </div>
      )}
      <div className="calculations-preview">
        <div className="calculation-item">
          <label>Salaire de base proratisé (aperçu):</label>
          <span className="amount">{calculations.baseProrated} TND</span>
        </div>
        <div className="calculation-item">
          <label>Salaire brut:</label>
          <span className="amount">{calculations.grossSalary} TND</span>
        </div>
        {simulationResult && simulationResult.netSalary !== undefined && (
          <div className="calculation-item">
            <label>Salaire net:</label>
            <span className="amount net">
              {`${(simulationNetPayable ?? simulationResult.netSalary ?? 0).toFixed(3)} TND`}
            </span>
          </div>
        )}
      </div>
      <div className="form-group">
        <label>Notes:</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows="3"
        />
      </div>

      {errors.submit && (
        <div className="error-banner">
          {errors.submit}
        </div>
      )}

      {!batchMode && (
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={isPeriodClosed}>
            {payroll ? 'Mettre à jour' : 'Créer'}
          </button>
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Annuler
          </button>
        </div>
      )}
    </form>
  );
  }
);

export default PayrollForm;

